import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AppLayout, GlassCard, PageHeader, LoadingSpinner } from '../components/Layout';
import ScoreRing from '../components/ScoreRing';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, TrendingUp, AlertTriangle, CheckCircle, Wallet, Briefcase, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PageArrival from '../components/PageArrival';
import CareerTargetLock from '../components/CareerTargetLock';
import KnownFactsStrip from '../components/KnownFactsStrip';
import MajorChangePanel from '../components/MajorChangePanel';
import { useFPContext } from '../context/FuturePathContext';
import { Map as A1, Briefcase as A2, Layers as A3, Wallet as A4, Clock as A5, Compass as A6 } from 'lucide-react';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-slate-300 mb-1.5">{label}</label>
    {children}
  </div>
);

const Chips = ({ items, tone }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map(i => (
      <span key={i} className={`px-2 py-0.5 rounded-full text-[11px] border ${tone}`}>{i}</span>
    ))}
  </div>
);

export default function TransitionIntelligencePage() {
  const [arrived, setArrived] = useState(false);
  const { user, getAuthHeader } = useAuth();
  const { traits, mayAsk, askInstead, askReason, known } = useFPContext();
  const [types, setTypes] = useState([]);
  const [kind, setKind] = useState(null);
  const [because, setBecause] = useState('');
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [building, setBuilding] = useState(false);
  const [form, setForm] = useState({
    current_role: '', experience_summary: '', years_experience: '',
    target_career: '', min_income: '', savings_months: '',
  });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/intelligence/transition`, getAuthHeader());
      if (res.data?.plan) {
        setPlan(res.data.plan);
        setForm(f => ({
          ...f,
          current_role: res.data.plan.current_state?.role === 'Not specified' ? '' : res.data.plan.current_state?.role || '',
          years_experience: res.data.plan.current_state?.years_experience ?? '',
          target_career: res.data.plan.target?.occupation || '',
          min_income: res.data.plan.current_state?.min_income ?? '',
        }));
      } else {
        // Never ask for something the platform already holds.
        setForm(f => ({
          ...f,
          target_career: user?.target_career || user?.career_aspirations || '',
          current_role: f.current_role || known('occupation') || '',
          min_income: f.min_income || known('income_floor') || '',
        }));
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, user, known]);

  useEffect(() => {
    // Which kind of move is this? Resolved from the canonical context, not guessed per page.
    (async () => {
      try {
        const r = await axios.get(`${API}/transition/types`, getAuthHeader());
        setTypes(r.data?.types || []);
        setBecause(r.data?.because || '');
        setKind(prev => prev || r.data?.recommended || null);
      } catch { /* the bridge builder still works on its own */ }
    })();
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const build = async () => {
    // No red toast for something the person hasn't been asked yet, the page says it calmly
    // above the button instead.
    if (!form.target_career.trim()) return;
    setBuilding(true);
    try {
      const res = await axios.post(`${API}/intelligence/transition`, {
        current_role: form.current_role,
        experience_summary: form.experience_summary,
        years_experience: form.years_experience ? Number(form.years_experience) : null,
        target_career: form.target_career,
        min_income: form.min_income ? Number(form.min_income) : null,
        savings_months: form.savings_months ? Number(form.savings_months) : null,
      }, { ...getAuthHeader(), timeout: 55000 });

      if (!res.data.ready) {
        toast.error(res.data.message);
        setPlan(null);
      } else {
        setPlan(res.data.plan);
        toast.success('Transition plan built from federal occupation and wage data.');
      }
    } catch {
      toast.error('Could not build the plan. Try again.');
    } finally {
      setBuilding(false);
    }
  };

  if (loading) {
    return <AppLayout><div className="flex items-center justify-center h-64"><LoadingSpinner size="lg" /></div></AppLayout>;
  }

  if (!arrived) return (
    <AppLayout>
      <PageArrival
        testid="transition-arrival"
        eyebrow="Transition Intelligence"
        title="The bridge from where you are to where you're going."
        subtitle="You don't start over. This works out what you already carry across, what's genuinely missing, and the shortest credible route between the two, with real wages at both ends, so you can see whether the move pays."
        accent="#22d3ee"
        second="#a78bfa"
        ctaLabel="Build my bridge"
        narration={"This is Transition Intelligence. Nobody starts from zero. We take what you do now, the role, the industry, the language of it, and work out which of those skills the target job already wants. Then we name what's actually missing, and lay out the bridge: the steps, the order, and roughly how long each one takes. You'll see real wages at both ends and whether the move clears the income your life takes. And if the target is a reach today, we show you the adjacent roles that get you most of the way."}
        facts={[
          { icon: A3, head: 'Skills match, scored', sub: 'What transfers, in the target\u2019s own language' },
          { icon: A5, head: 'A bridge with a timeline', sub: 'Steps in order, not a wish list' },
          { icon: A4, head: 'Wages at both ends', sub: 'Real BLS medians against your income floor' },
        ]}
        modulesTitle="What this page works out"
        modules={[
          { icon: A2, label: 'What you already carry', line: 'Transferable skills pulled from your role and, for veterans, your service.', hue: '#22d3ee' },
          { icon: A6, label: 'What the target needs', line: 'Occupation-specific requirements, not vague field defaults.', hue: '#a78bfa' },
          { icon: A3, label: 'The gap, named', line: 'Only what\u2019s genuinely missing, with how to close each one.', hue: '#fbbf24' },
          { icon: A1, label: 'The recommended bridge', line: 'Steps, order and timeline, the shortest credible route.', hue: '#34d399' },
          { icon: A4, label: 'Does it pay?', line: 'Target wages against the number your life takes.', hue: '#f472b6' },
          { icon: A5, label: 'Adjacent roles', line: 'Nearby jobs that get you most of the way, sooner.', hue: '#60a5fa' },
        ]}
        onStart={() => { setArrived(true); window.scrollTo({ top: 0 }); }}
      />
    </AppLayout>
  );

  const activeType = types.find(t => t.key === kind) || null;

  return (
    <AppLayout>
      <PageHeader
        title="Transition Intelligence"
        description="Current role → what already transfers → what's missing → the bridge → who pays for it."
      />

      {types.length > 0 && (
        <div className="mb-5" data-testid="transition-types">
          <p className="text-[10px] uppercase tracking-[0.16em] text-slate-300 mb-2">
            Which move are you making?{because ? <span className="text-slate-300 normal-case tracking-normal"> · {because}</span> : null}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {types.map(t => {
              const on = t.key === kind;
              return (
                <button key={t.key} onClick={() => setKind(t.key)} data-testid={`transition-type-${t.key}`}
                  title={t.question}
                  className={`relative px-4 py-2 rounded-full text-sm transition-colors ${
                    on ? 'text-slate-950 font-semibold' : 'text-slate-300 hover:text-white'}`}>
                  {on && <span className="absolute inset-0 rounded-full bg-cyan-300" aria-hidden />}
                  {!on && <span className="absolute inset-0 rounded-full border border-slate-700 hover:border-slate-500 transition-colors" aria-hidden />}
                  <span className="relative">{t.label}</span>
                </button>
              );
            })}
          </div>
          {activeType?.question && (
            <p className="text-sm text-slate-300 mt-3">{activeType.question}</p>
          )}
        </div>
      )}

      {activeType?.engine === 'major_change' && <MajorChangePanel />}

      {activeType?.engine !== 'major_change' && <>
      <CareerTargetLock className="mb-4" />
      {!form.target_career.trim() && (
        <p className="mb-4 text-sm text-slate-300" data-testid="transition-needs-target">
          Set your target occupation above and this builds the bridge to it, the skills that carry
          over, the ones you'd need, and what the move costs while you make it.
        </p>
      )}
      <KnownFactsStrip className="mb-4" keys={['state', 'education_stage', 'employment_status', 'occupation', 'income_floor', 'skills_count']} />

      <GlassCard className="mb-6" data-testid="transition-form">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label={traits?.has_career ? 'Where you are now' : 'What you are doing right now'}>
            <Input value={form.current_role} onChange={e => setForm({ ...form, current_role: e.target.value })}
              placeholder={traits?.has_career ? 'e.g. Bank Operations Manager' : 'e.g. High school senior, part-time at a cafe'}
              data-testid="input-current-role" />
          </Field>
          {mayAsk('years_experience') ? (
            <Field label="Years of experience">
              <Input type="number" value={form.years_experience} onChange={e => setForm({ ...form, years_experience: e.target.value })}
                placeholder="15" data-testid="input-years" />
            </Field>
          ) : (
            <Field label={askInstead('years_experience') || 'What you have done so far'}>
              <Input value={form.experience_summary} onChange={e => setForm({ ...form, experience_summary: e.target.value })}
                placeholder="School, sport, volunteering, a part-time job" data-testid="input-years" />
              <p className="text-[11px] text-slate-300 mt-1">{askReason('years_experience')}</p>
            </Field>
          )}
          <Field label={traits?.employed ? 'Minimum income you must keep' : 'Income you need this to reach'}>
            <Input type="number" value={form.min_income} onChange={e => setForm({ ...form, min_income: e.target.value })}
              placeholder="95000" data-testid="input-min-income" />
          </Field>
          <div className="md:col-span-2">
            <Field label="What you've actually done (the more detail, the better the skill translation)">
              <textarea
                value={form.experience_summary}
                onChange={e => setForm({ ...form, experience_summary: e.target.value })}
                rows={3}
                data-testid="input-experience"
                placeholder="Managed operations, risk and compliance teams. Led process improvement and vendor coordination."
                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-500"
              />
            </Field>
          </div>
        </div>
        <Button className="mt-4" onClick={build} disabled={building || !form.target_career.trim()} data-testid="build-plan-btn">
          {building ? 'Building…' : <>Build My Transition Plan <ArrowRight className="w-4 h-4 ml-1" /></>}
        </Button>
      </GlassCard>
      </>}

      {plan && (
        <div className="space-y-6" data-testid="transition-plan">
          {/* Headline */}
          <GlassCard className="fp-rail">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[220px]">
                <p className="text-[11px] uppercase tracking-wider text-slate-300">Current state</p>
                <p className="text-white font-semibold">{plan.current_state?.role}</p>
                <p className="text-xs text-slate-300">{(plan.current_state?.archetypes || []).map(a => a.label).join(' · ')}</p>
              </div>
              <ArrowRight className="w-5 h-5 text-cyan-400" />
              <div className="flex-1 min-w-[220px]">
                <p className="text-[11px] uppercase tracking-wider text-slate-300">Target</p>
                <p className="text-white font-semibold">{plan.target?.occupation}</p>
                <p className="text-xs text-slate-300">{plan.target?.field} · SOC {plan.target?.soc_code}</p>
              </div>
              <ScoreRing value={plan.skills_match_percent} label="Skills match" palette="cyan" size={150} testId="skills-match" />
            </div>
          </GlassCard>

          <div className="grid md:grid-cols-2 gap-6">
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" /> Skills that already transfer
              </h3>
              <Chips items={plan.transferable_skills || []} tone="bg-emerald-500/10 border-emerald-500/30 text-emerald-200" />
              {plan.evidence_backed_skills?.length > 0 && (
                <p className="text-xs text-slate-300 mt-3">{plan.evidence_backed_skills.length} of these are backed by evidence in your wallet, those are the ones employers can verify.</p>
              )}
            </GlassCard>
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" /> What's missing
              </h3>
              <Chips items={plan.missing_skills || []} tone="bg-amber-500/10 border-amber-500/30 text-amber-200" />
            </GlassCard>
          </div>

          {/* Bridge */}
          <GlassCard>
            <h3 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
              <Clock className="w-4 h-4 text-cyan-300" /> Recommended bridge
            </h3>
            <p className="text-sm text-cyan-200">{plan.bridge?.label}</p>
            <p className="text-xs text-slate-300 mb-3">Estimated transition: {plan.bridge?.estimated_timeline}</p>
            <ol className="space-y-2">
              {(plan.bridge?.steps || []).map((st, i) => (
                <li key={i} className="flex gap-3 text-sm text-slate-200">
                  <span className="w-5 h-5 rounded-full bg-cyan-500/20 text-cyan-300 text-xs flex items-center justify-center shrink-0">{i + 1}</span>
                  {st}
                </li>
              ))}
            </ol>
          </GlassCard>

          {/* Outlook + wages */}
          <div className="grid md:grid-cols-2 gap-6">
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-cyan-300" /> Outlook on the destination
              </h3>
              <p className="text-sm text-white">{plan.outlook?.growth} · wages {plan.outlook?.wage_trend}</p>
              <p className="text-xs text-slate-300 mt-1">AI/automation task exposure: {plan.outlook?.ai_exposure}/100</p>
              <p className="text-sm text-slate-300 mt-2">{plan.outlook?.what_it_means}</p>
            </GlassCard>
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3">What it pays</h3>
              {plan.salary?.median ? (
                <>
                  <p className="text-2xl font-bold text-white">${Math.round(plan.salary.median).toLocaleString()}</p>
                  <p className="text-xs text-slate-300">Median · 25th-75th: ${Math.round(plan.salary.p25 || 0).toLocaleString()}-${Math.round(plan.salary.p75 || 0).toLocaleString()}</p>
                  {plan.income_gap_vs_floor != null && (
                    <p className={`text-sm mt-2 ${plan.income_gap_vs_floor >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {plan.income_gap_vs_floor >= 0
                        ? `Clears your income floor by $${plan.income_gap_vs_floor.toLocaleString()}`
                        : `Falls $${Math.abs(plan.income_gap_vs_floor).toLocaleString()} short of your income floor`}
                    </p>
                  )}
                  <p className="text-[11px] text-slate-300 mt-2">{plan.salary.source}</p>
                </>
              ) : (
                <p className="text-sm text-slate-300">Federal wage data isn't available for this SOC code right now.</p>
              )}
            </GlassCard>
          </div>

          {/* Funding */}
          {plan.funding?.length > 0 && (
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-300" /> Who could pay for the bridge
              </h3>
              <div className="grid md:grid-cols-2 gap-3">
                {plan.funding.map(f => (
                  <a key={f.key} href={f.url} target="_blank" rel="noreferrer"
                    className="p-3 rounded-lg border border-slate-700 bg-slate-800/40 hover:border-emerald-400/50 transition-colors"
                    data-testid={`funding-${f.key}`}>
                    <p className="text-sm font-medium text-white">{f.name}</p>
                    <p className="text-[11px] uppercase tracking-wider text-emerald-300 mt-0.5">{f.kind}</p>
                    <p className="text-xs text-slate-300 mt-1">{f.covers}</p>
                    <p className="text-xs text-slate-300 mt-1">Who: {f.who}</p>
                  </a>
                ))}
              </div>
            </GlassCard>
          )}

          {/* Adjacent roles */}
          {plan.adjacent_roles?.length > 0 && (
            <GlassCard>
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-slate-300" /> These same skills also land here
              </h3>
              <div className="flex flex-wrap gap-2">
                {plan.adjacent_roles.map(r => (
                  <span key={r.soc_code} className="px-3 py-1.5 rounded-full text-xs bg-slate-800 border border-slate-700 text-slate-200">{r.title}</span>
                ))}
              </div>
            </GlassCard>
          )}

          <p className="text-xs text-slate-300">Built from: {(plan.sources || []).join(' · ')}.</p>
        </div>
      )}
    </AppLayout>
  );
}
